import { useState } from 'react';
import PropTypes from 'prop-types';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import InputBase from '@/components/core/form/InputBase';
import { parseErrorMessage } from '@/utils/errorUtils';
import { unitToString } from '@/utils/unitUtils';
import { caseActions } from '@/stores/caseStore';
import { toast } from 'react-toastify';

const CustomPropertyDialog = ({
  outputLabel,
  outputName,
  nodeId,
  caseNodeCustomProperties,
  customProperties,
  onClose,
}) => {
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [value, setValue] = useState('0');
  const [isSaving, setIsSaving] = useState(false);

  const propertyOptions = customProperties.map(property => ({
    value: property.name,
    label: property.label,
  }));
  const selectedProperty = customProperties.find(property => property.name === selectedPropertyName) ?? null;

  const onPropertyChange = event => {
    const propertyName = event.target.value;
    setSelectedPropertyName(propertyName);

    if (!propertyName) {
      setValue('0');
      return;
    }

    const existingValue = caseNodeCustomProperties?.[outputName]?.[propertyName];
    setValue(existingValue?.toString() ?? '0');
  };

  const unitLabel = selectedProperty ? unitToString(selectedProperty.unit) : '';
  const isSaveDisabled = !selectedProperty || value === '' || Number.isNaN(Number(value)) || isSaving;

  const onSave = async () => {
    if (!selectedProperty) {
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      toast.error('Enter a valid numeric value.');
      return;
    }

    setIsSaving(true);

    try {
      await caseActions.setNodeCustomProperties(nodeId, {
        ...(caseNodeCustomProperties ?? {}),
        [outputName]: {
          ...(caseNodeCustomProperties?.[outputName] ?? {}),
          [selectedProperty.name]: numericValue,
        },
      });
      onClose();
    } catch (error) {
      parseErrorMessage(error).forEach(message => toast.error(message));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <dialog className="modal" open>
      <div className="modal-box max-w-sm">
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Cog6ToothIcon className="h-5 w-5 text-gray-500 shrink-0" />
            <h3 className="font-bold text-lg">Set Custom Property: {outputLabel}</h3>
          </div>

          <div className="space-y-2">
            <label className="label-text">Custom Property</label>
            <select
              value={selectedPropertyName}
              onChange={onPropertyChange}
              className="select select-bordered select-sm w-full"
            >
              <option value=""></option>
              {propertyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {selectedProperty && (
            <div className="space-y-2">
              <label className="label-text">Value</label>
              <InputBase
                type="number"
                value={value}
                onChange={event => setValue(event.target.value)}
                className="!w-full"
              />
              <div className="text-sm text-gray-500">Unit: {unitLabel}</div>
            </div>
          )}

          <div className="modal-action justify-end gap-2">
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-accent btn-sm ${isSaveDisabled ? 'btn-disabled' : ''}`}
              disabled={isSaveDisabled}
              onClick={onSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
};

CustomPropertyDialog.propTypes = {
  outputLabel: PropTypes.string.isRequired,
  outputName: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
  caseNodeCustomProperties: PropTypes.object,
  customProperties: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CustomPropertyDialog;
